export const STANDARDS = [
  {
    code: "ESAIA 01:202601",
    slug: "esaia-01-202601",
    title: "AI Assurance Framework",
    subtitle: "ESAIA Standard 01 | AI Assurance Framework",
    version: "202601",
    issued: "15 January 2026",
    status: "Active",
    download: "/downloads/ESAIA-01-202601-Standard.pdf",
    purpose:
      "Provides organizations with a structured, independent basis for assessing responsible AI practice across governance, risk, oversight and operations.",
    groups: [
      {
        part: "Part I",
        title: "Foundations",
        items: [
          { n: "01", title: "Purpose", copy: "Defines the objective of the ESAIA framework: providing organizations with a structured, independent basis for assessing responsible AI practice." },
          { n: "02", title: "Scope", copy: "Describes the organizations, AI systems and AI agents to which the standard applies, including individual entities and corporate groups." },
          { n: "03", title: "Principles", copy: "Sets out the core principles behind the framework: transparency, accountability and ethical responsibility across the AI lifecycle." },
        ],
      },
      {
        part: "Part II",
        title: "Assessment practice",
        items: [
          { n: "04", title: "Assessment methodology", copy: "Outlines how an ESAIA assessment is structured, from scoping and evidence collection to findings, reporting and the certification decision." },
          { n: "05", title: "Risk assessment", copy: "Covers the identification, mapping and treatment of AI-related risks across operations, data, oversight and implementation." },
        ],
      },
      {
        part: "Part III",
        title: "Governance and oversight",
        items: [
          { n: "06", title: "Governance", copy: "Addresses governance structures, ownership, roles and decision rights for the development, deployment and use of AI." },
          { n: "07", title: "Human oversight", copy: "Defines expectations for meaningful human oversight, escalation paths and intervention capability for AI systems and agents." },
          { n: "08", title: "Accountability", copy: "Sets expectations for demonstrable accountability: named ownership, documented responsibility and traceable decisions." },
        ],
      },
      {
        part: "Part IV",
        title: "Operation and improvement",
        items: [
          { n: "09", title: "Transparency", copy: "Addresses transparency around AI use, including inventories, communication to affected parties and visibility into decision-making." },
          { n: "10", title: "Documentation", copy: "Covers the documentation required to evidence responsible AI practice, from system records to governance registers." },
          { n: "11", title: "Monitoring", copy: "Addresses ongoing monitoring of AI systems and agents, including change detection, logging and periodic internal review." },
          { n: "12", title: "Continuous improvement", copy: "Defines the improvement loop: remediation of findings, progress tracking and preparation for annual reassessment." },
        ],
      },
    ],
  },
  {
    code: "ESAIA 02:202603",
    slug: "esaia-02-202603",
    title: "AI Data Governance & Quality",
    subtitle:
      "ESAIA Standard 02 | Requirements for Responsible Data Management in Artificial Intelligence Systems",
    version: "202603",
    issued: "23 March 2026",
    status: "Active",
    download: "/downloads/ESAIA-02-202701-Standard.pdf",
    purpose:
      "Establishes voluntary requirements for assessing the governance, quality and lifecycle management of AI data, in support of ethical, accountable and auditable AI.",
    groups: [
      {
        part: "Part I",
        title: "Foundations",
        items: [
          { n: "01", title: "Purpose", copy: "Establishes voluntary requirements for assessing the governance, quality and lifecycle management of AI data, helping organizations evaluate, document and improve their data practices." },
          { n: "02", title: "Scope", copy: "Applies to organizations deploying AI systems with structured data or datasets, assurance bodies performing voluntary data assessments, and internal governance, risk or AI ethics teams." },
          { n: "03", title: "Principles of data governance assurance", copy: "Sets out the guiding principles: accountability, integrity, privacy and consent, fairness and bias mitigation, transparency, proportionality and confidentiality." },
        ],
      },
      {
        part: "Part II",
        title: "Assessment practice",
        items: [
          { n: "04", title: "Data assessment process requirements", copy: "Defines the stages of a data assessment: planning, governance assessment, lifecycle evaluation, bias and fairness review, privacy and security review, and post-deployment evaluation." },
          { n: "05", title: "Data lifecycle management", copy: "Covers acquisition, preprocessing, annotation, retention and deletion of AI data across its full lifecycle." },
          { n: "06", title: "Bias detection and fairness", copy: "Addresses the detection and mitigation of bias and the evaluation of fairness in datasets and data practices." },
          { n: "07", title: "Privacy protection and consent", copy: "Covers privacy protection and consent management within AI data management practices." },
        ],
      },
      {
        part: "Part III",
        title: "Evidence and reporting",
        items: [
          { n: "08", title: "Evidence and documentation requirements", copy: "Specifies the evidence needed to demonstrate good governance, quality and ethical data practices, including data handling documentation and quality metrics." },
          { n: "09", title: "Reporting and outputs", copy: "Describes how findings are classified, which assessment outputs are produced and which disclaimers are required in communication." },
        ],
      },
      {
        part: "Part IV",
        title: "Independence and improvement",
        items: [
          { n: "10", title: "Independence and conflict management", copy: "Sets requirements for assessor independence and the management of conflicts of interest during an assessment." },
          { n: "11", title: "Monitoring and continuous evaluation", copy: "Covers monitoring and post-deployment evaluation of data practices so that assurance keeps pace with change." },
        ],
      },
    ],
  },
  {
    code: "ESAIA 03:202607",
    slug: "esaia-03-202607",
    title: "High-Risk AI System Assurance",
    subtitle:
      "ESAIA Standard 03 | Requirements for Ethical, Operational and Governance Assessment",
    version: "202607",
    issued: "15 July 2026",
    status: "Active",
    download: "/downloads/ESAIA-03-202607-Standard.pdf",
    purpose:
      "Establishes voluntary requirements for the independent assessment of high-risk AI systems, providing a structured framework to evaluate governance, operational controls, data and model quality, human oversight and risk mitigation.",
    groups: [
      {
        part: "Part I",
        title: "Foundations",
        items: [
          { n: "01", title: "Purpose", copy: "Establishes voluntary requirements for the independent assessment of high-risk AI systems, providing organizations with a structured framework to evaluate governance, operational controls, data and model quality, human oversight and risk mitigation." },
          { n: "02", title: "Scope", copy: "Applies to organizations deploying AI systems categorized as high-risk under EU AI Act definitions, assurance bodies performing voluntary high-risk AI assessments, and internal governance or AI ethics teams." },
          { n: "03", title: "Principles of high-risk AI assurance", copy: "Sets out the guiding principles: independence, impartiality, transparency, proportionality, accountability, human oversight and confidentiality." },
        ],
      },
      {
        part: "Part II",
        title: "Assessment practice",
        items: [
          { n: "04", title: "Assessment process requirements", copy: "Defines planning and scoping: identifying the AI systems, functions and processes in scope, determining key stakeholders and responsible parties, and documenting exclusions and limitations." },
          { n: "05", title: "Governance and accountability review", copy: "Evaluates governance policies, oversight committees and escalation procedures, and verifies clearly assigned accountability for AI operations." },
          { n: "06", title: "Risk identification and mitigation", copy: "Assesses procedures for identifying ethical, operational, safety and societal risks, and reviews mitigation strategies and risk acceptance thresholds." },
          { n: "07", title: "Human oversight and intervention", copy: "Examines human-in-the-loop and supervisory mechanisms, and evaluates intervention and escalation workflows." },
          { n: "08", title: "Data and model evaluation", copy: "Reviews data provenance, quality, consent and bias mitigation practices, and assesses model documentation, assumptions, intended use and limitations." },
        ],
      },
      {
        part: "Part III",
        title: "Evidence and reporting",
        items: [
          { n: "09", title: "Monitoring and post-deployment evaluation", copy: "Assesses monitoring of safety, fairness and internal policy compliance after deployment, including incident reporting and corrective action procedures." },
          { n: "10", title: "Evidence and documentation", copy: "Specifies sufficient, securely stored and retrievable evidence demonstrating governance, oversight, operational controls and data quality." },
          { n: "11", title: "Reporting and outputs", copy: "Describes findings classification (low, medium and high exposure) and the outputs: Gap Exposure Letters, non-certifying assessment reports and optional voluntary attestations." },
        ],
      },
      {
        part: "Part IV",
        title: "Independence and improvement",
        items: [
          { n: "12", title: "Independence and conflict management", copy: "Requires independence from operational roles, identification and mitigation of conflicts of interest, and rotation, exclusion or recusal where necessary." },
          { n: "13", title: "Relationship to the EU AI Act", copy: "Maps the assessment clauses to EU AI Act articles (governance Art. 17, risk Art. 9, human oversight Art. 14, data and models Arts. 10-11, monitoring Arts. 61-62) as informative references only; the standard remains voluntary and non-certifying." },
        ],
      },
    ],
  },
];
